import { cuiMethods } from 'cui';
import type HeaderTabsContainer from 'Modules/mainWindowCuiLayout/HeaderTabsContainer';
import type MainWindowCuiLayout from '../../mainWindowCuiLayout/MainWindowCuiLayout';

type TabsState = MainWindowCuiLayout['state']['splitScreen'];

const openStartItems = async (mainLayout: MainWindowCuiLayout) => {
	const itemsToOpen: TabsState = aras.evalMethod('selectStartPage');
	const { tabsContainer } = mainLayout;
	if (!tabsContainer) {
		return;
	}

	const { mainTabs, dockTabs, tabs } = itemsToOpen;
	const cuiControl = mainLayout.navigationPanel.nav;
	const cuiOptions = mainLayout.options;
	const openPromises = [...mainTabs, ...dockTabs].map(async (tabId) => {
		const tabItem = tabs[tabId];
		const newId = await cuiMethods.openItemHandler(
			cuiControl,
			cuiOptions,
			tabItem
		);
		return { [tabId]: newId };
	});
	const newIds = await Promise.allSettled(openPromises);
	const oldIdToNewIdMap = newIds.reduce((acc, result) => {
		const newIdToOldId = result.status === 'fulfilled' ? result.value : null;

		return { ...acc, ...newIdToOldId };
	}, {});
	const updatedTabsState = getUpdatedTabsState(itemsToOpen, oldIdToNewIdMap);

	await updateTabsInState(updatedTabsState, tabsContainer.undockHeaderTabs);
	updateTabsDockStateAndOrder(updatedTabsState, tabsContainer);
	updateSelectedTabs(updatedTabsState, tabsContainer);
};

const getUpdatedTabsState = (
	oldTabsData: TabsState,
	oldIdToNewIdMap: Record<string, string>
): TabsState => {
	const { dockTabs, mainTabs, selectedTabs, tabs } = oldTabsData;
	const newDockTabs = dockTabs.map((tabId) => oldIdToNewIdMap[tabId]);
	const newMainTabs = mainTabs.map((tabId) => oldIdToNewIdMap[tabId]);
	const newSelectedTabs = selectedTabs.map((tabId) => oldIdToNewIdMap[tabId]);
	const newTabs = Object.entries(tabs).reduce((acc, [tabId, tab]) => {
		const newTabId = oldIdToNewIdMap[tabId];
		const parentTab = tab.parentTab ? oldIdToNewIdMap[tab.parentTab] : null;

		return {
			...acc,
			[newTabId]: { ...tab, parentTab }
		};
	}, {});

	return {
		dockTabs: newDockTabs,
		mainTabs: newMainTabs,
		selectedTabs: newSelectedTabs,
		tabs: newTabs
	};
};

const updateTabsDockStateAndOrder = (
	updatedTabsState: TabsState,
	tabsContainer: HeaderTabsContainer
) => {
	const { dockTabs, mainTabs } = updatedTabsState;
	const { undockHeaderTabs } = tabsContainer;

	dockTabs.forEach((tabId) => tabsContainer.dockTab(tabId));
	mainTabs.forEach((tabId, i) => {
		const tabWasSelected = undockHeaderTabs.selectedTab === tabId;
		const data = undockHeaderTabs.ejectTab(tabId);
		data && undockHeaderTabs.addTab(tabId, data, i);
		tabWasSelected && undockHeaderTabs.selectTab(tabId);
	});
};

const updateTabsInState = (
	updatedTabsState: TabsState,
	headerTabs: HeaderTabsContainer['undockHeaderTabs']
) => {
	const { dockTabs, mainTabs, tabs } = updatedTabsState;

	return Promise.allSettled([
		...dockTabs.map((id) => headerTabs.setTabContent(id, tabs[id])),
		...mainTabs.map((id) => headerTabs.setTabContent(id, tabs[id]))
	]);
};

const updateSelectedTabs = (
	updatedTabsState: TabsState,
	tabsContainer: HeaderTabsContainer
) => {
	const { dockHeaderTabs, undockHeaderTabs } = tabsContainer;
	updatedTabsState.selectedTabs.forEach((tabId) => {
		const isDocked = tabsContainer.isDockedTab(tabId);
		const headerTabs = isDocked ? dockHeaderTabs : undockHeaderTabs;
		const tabExists = headerTabs.tabs.includes(tabId);
		tabExists && headerTabs.selectTab(tabId);
	});
};

export { openStartItems };
